import isNil from 'lodash/isNil';
import { parseISO } from 'date-fns';
import { formatWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import type { BaseUrl, ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { toReadableHours } from '../time';
import type {
	WorkingHoursRestResponse,
	WorkingHours,
	CalendarsRestResponse,
	CalendarRestResponse,
	Calendars,
	Calendar,
	WorkingTimesRestResponse,
	WorkingTimes,
	HolidaysRestResponse,
	Holidays,
	TimeZoneRestResponse,
	TimeZonesRestResponse,
	TimeZone,
	TimeZones,
} from './types';

export const getUrl = (baseUrl: BaseUrl, projectKey: ProjectKey): string =>
	`${baseUrl}/rest/workinghours/1/internal/dialog/${projectKey}`;

export const mapTimezoneToSelectOption = ({ id, displayName }: TimeZoneRestResponse): TimeZone => ({
	label: displayName,
	value: id,
});

export const mapTimezoneToSelectOptions = (timezones: TimeZonesRestResponse): TimeZones =>
	timezones.map(mapTimezoneToSelectOption);

export const mapHolidaysRestResponseToHolidays = (
	holidaysRestResponse: HolidaysRestResponse,
): Holidays =>
	holidaysRestResponse.map(({ recurring, ...holidayData }) => ({
		...holidayData,
		isRecurring: recurring,
	}));

export const mapWorkingTimesRestResponseToWorkingTimes = (
	workingTimesRestResponse: WorkingTimesRestResponse,
): WorkingTimes =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	workingTimesRestResponse.reduce<Record<string, any>>(
		(workingTimesReadOnly, { weekday, start, end }) => {
			const workingTimes = workingTimesReadOnly;

			if (isNil(workingTimes[weekday])) {
				workingTimes[weekday] = [];
			}
			workingTimes[weekday].push({
				start: toReadableHours(start),
				end: toReadableHours(end),
			});

			return workingTimes;
		},
		{},
	);

export const transformCalendarResponse = ({
	workingTimes,
	holidays,
	...calendarData
}: CalendarRestResponse): Calendar => ({
	...calendarData,
	workingTimes: mapWorkingTimesRestResponseToWorkingTimes(workingTimes),
	holidays: mapHolidaysRestResponseToHolidays(holidays),
});

export const transformCalendarsResponse = (request: CalendarsRestResponse): Calendars =>
	request.map(transformCalendarResponse);

export const transformResponse = ({
	calendars,
	allTimezones,
	defaultTimezone,
	...rest
}: WorkingHoursRestResponse): WorkingHours => ({
	allTimezones: mapTimezoneToSelectOptions(allTimezones),
	defaultTimezone: mapTimezoneToSelectOption(defaultTimezone),
	calendars: transformCalendarsResponse(calendars),
	...rest,
});

export const formatDate = ({
	iso8601Date,
	locale,
	isRecurring,
	message,
}: {
	iso8601Date: string;
	locale: string;
	isRecurring: boolean;
	message: string;
}) => {
	// date-fns doesn't support long localized date without year (https://date-fns.org/v2.27.0/docs/format)
	const pattern = isRecurring ? 'dd MMM' : 'PP';
	// If iso8601Date is undefined, new Date(iso8601Date) will throw an error. moment.js returns current date in such a scenario.
	// Required for cypress tests. In a normal scenario, iso8601Date will never be undefined.
	const parsedDate = iso8601Date ? parseISO(iso8601Date) : new Date();
	const localizedDate = formatWithLocale(parsedDate, pattern, locale);

	return isRecurring ? `${message} ${localizedDate}` : `${localizedDate}`;
};

export const sortHolidays = (holidays: Holidays) => {
	// Recurring holidays on top, sorted by only mm-dd part
	const recurringHolidays = holidays
		.filter((holiday) => holiday.isRecurring)
		.sort((a, b) => a.iso8601Date.substring(5).localeCompare(b.iso8601Date.substring(5)));

	const singleDayHolidays = holidays
		.filter((holiday) => !holiday.isRecurring)
		.sort((a, b) => a.iso8601Date.localeCompare(b.iso8601Date));

	return [...recurringHolidays, ...singleDayHolidays];
};

// Calendar edit TimePicker constants
export const TWENTYFOUR_HOUR_FORMAT = 'HH:mm';
export const ENGLISH_US_LOCALE = 'en_US';

export const getTimeFormatForTimePicker = (locale: string): string | null =>
	locale === ENGLISH_US_LOCALE ? TWENTYFOUR_HOUR_FORMAT : null;
